import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleInfo, faInfo, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Placement, createPopper } from "@popperjs/core";
import React, { useRef, useState } from "react";
import { GPS } from "../../../util/geo";
import { More } from "../../components/MoreToggle";

export const fa = (name: IconProp, ...attributes) =>
  function Component({ ...props }) {
    // @ts-ignore
    return <FontAwesomeIcon icon={name} {...props} {...attributes} />;
  };

const ExternalLinkIcon = ({ ...props }) => (
  <FontAwesomeIcon icon={faUpRightFromSquare} {...props} size="xs" />
);

export const Info = ({ as = "div", divide = true, ...props }) => (
  <div className="flex flex-column items-stretch text-sm">
    {React.createElement(as, {
      className: `rounded-sm border-gray border w-full${divide ? " divide-y divide-gray" : ""}`,
      ...props,
    })}
  </div>
);

interface KVProps extends React.HTMLAttributes<HTMLDivElement> {
  k: string;
  children: React.ReactNode;
  sub?: boolean;
  check?: any;
  className?: string;
}
export const KV = ({ k, children, sub, check = true, className, ...props }: KVProps) =>
  check ? (
    <div className={`flex p-2 ${className ?? ""}`} {...props}>
      <dt className={"w-2/5" + (sub ? "" : " font-semibold")}>{k}</dt>
      <dd className="w-3/5">{children}</dd>
    </div>
  ) : null;

/**
 * Een uitklapbare regel binnen een `Info`-blok, zoals Voorzieningen: de kop met
 * `summary` en de "meer"-knop; open wordt de lijn eronder gestippeld en staan de
 * details ingesprongen eronder, samen in één blok zodat er geen lijn tussen
 * elke subregel komt. Zet de details in `SubKV`'s. De details worden pas
 * gerenderd als hij open is, dus een zware bron (OSM) laadt dan pas.
 *
 * (Tailwind 4: de scheidingslijn van `divide-y` is de onderrand van een rij, dus
 * `border-dashed` op de kop maakt de lijn eronder gestippeld.)
 */
export const FoldKV = ({
  k,
  summary,
  children,
}: {
  k: string;
  summary: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <KV k={k} className={open ? "border-dashed" : ""}>
        <More open={open} setOpen={setOpen} />
        {summary}
      </KV>
      {open && <div className="text-sm py-1">{children}</div>}
    </>
  );
};

/** Eén ingesprongen regel onder een `FoldKV`. */
export const SubKV = ({ k, children }: { k: React.ReactNode; children: React.ReactNode }) => (
  <div className="flex pb-1 pl-4">
    <dt className="w-2/5">{k}</dt>
    <dd className="w-3/5">{children}</dd>
  </div>
);

const modifiers = [
  {
    name: "offset",
    options: {
      offset: [0, 8],
    },
  },
];

function timeoutIf(func, timeout, useTimeout) {
  if (!useTimeout) func();
  else return setTimeout(func, timeout);
}

// https://www.creative-tim.com/learning-lab/tailwind-starter-kit/documentation/react/tooltips/bottom
interface TooltipProps {
  className?: string;
  content: React.ReactNode | string;
  children?: React.ReactNode;
  placement?: Placement;
  timeout?: number;
}
export const Tooltip = ({
  className,
  content,
  children,
  placement = "bottom",
  timeout,
}: TooltipProps) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeOutRef = useRef<ReturnType<typeof setInterval> | undefined>(null);
  const onStart = (e) => {
    const s = tooltipRef.current?.style;
    clearTimeout(timeOutRef.current);
    if (!s || s.display === "block") return;
    timeOutRef.current = timeoutIf(
      () => {
        s.display = "block";
        createPopper(btnRef.current, tooltipRef.current, { placement, modifiers });
      },
      timeout || 400,
      e.type === "mousestart"
    );
  };
  const onMouseLeave = (e) => {
    const s = tooltipRef.current?.style;
    clearTimeout(timeOutRef.current);
    if (!s || s.display === "none") return;
    timeOutRef.current = timeoutIf(
      () => {
        s.display = "none";
      },
      timeout || 750,
      e.type === "mouseleave"
    );
  };

  return (
    <>
      <button
        type="button"
        onTouchStart={onStart}
        onMouseEnter={onStart}
        onMouseLeave={onMouseLeave}
        onTouchEnd={onMouseLeave}
        className={className}
        ref={btnRef}
      >
        {children}
      </button>
      {/* Verborgen tot onStart display:block zet; de pijl is een gedraaid
          vierkant dat via de group-data-variant naar de popper-zijde schuift. */}
      <div
        className="group hidden bg-dark-gray text-white p-3 rounded-lg text-sm max-w-xs text-left no-underline break-words"
        ref={tooltipRef}
      >
        {content}
        <div
          data-popper-arrow={true}
          className="absolute invisible w-2 h-2 bg-inherit before:visible before:content-[''] before:absolute before:w-2 before:h-2 before:bg-inherit before:rotate-45 group-data-[popper-placement^=top]:-bottom-1 group-data-[popper-placement^=bottom]:-top-1 group-data-[popper-placement^=left]:-right-1 group-data-[popper-placement^=right]:-left-1"
        />
      </div>
    </>
  );
};

export const ITooltip = ({ children, ...props }: TooltipProps) => (
  <Tooltip {...props}>
    <span className="underline decoration-dashed">{children}</span>{" "}
    <FontAwesomeIcon
      icon={faInfo}
      size="sm"
      className="pl-0.5 w-4 h-4 inline"
      fill="currentColor"
    />
  </Tooltip>
);

export const SectionHeader = ({ icon, title, description = undefined }) => (
  <div className="flex items-center py-2">
    <div className="bg-light-green rounded-xl w-9 h-9 flex items-center justify-center">{icon}</div>
    <h3 className="pl-2.5 font-bold text-2xl">{title}</h3>
    {description && (
      <Tooltip className="ml-auto text-gray-500 hover:text-dark" content={description}>
        <FontAwesomeIcon icon={faCircleInfo} className="w-6 h-6" fill="currentColor" />
      </Tooltip>
    )}
  </div>
);

export const SubHeader = ({ title = "", children }) => (
  <div>
    {title}
    <h3 className="font-bold text-lg leading-none my-1">{children}</h3>
  </div>
);

const baseClass =
  "w-full rounded-md bg-bright-yellow flex items-center justify-center font-bold text-sm p-4 hover:bg-yellow ";
export const BigButton = ({ className = "", children }) => (
  <button className={baseClass + (className || "")}>{children}</button>
);

export const nFormat = new Intl.NumberFormat("NL-nl", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
export const rFormat = new Intl.NumberFormat("NL-nl", { maximumFractionDigits: 0 });
export const fCoords = (coords: GPS, separator = ", ", precision = 5) =>
  `${coords[0].toFixed(precision)}${separator}${coords[1].toFixed(precision)}`;

// Expliciet getypt: met `children = undefined` als default leidde TypeScript
// af dat children altijd undefined moet zijn, en werd elke <A>tekst</A> een fout.
// Zonder children (alleen een icoon, zoals de kaartlink) mag ook.
type AProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children?: React.ReactNode;
  show?: boolean;
  Icon?: React.ElementType;
};

export const A = ({ children, show = true, Icon = ExternalLinkIcon, ...props }: AProps) =>
  !show ? null : (
    <a
      className="bg-transparent inline hover:underline text-sm aelement pr-0.25"
      rel="noopener noreferrer"
      target="_blank"
      {...props}
    >
      <span className="underline">{children}</span>{" "}
      <Icon className="inline" fill="currentColor" height={12} />
    </a>
  );

export const Links = ({ children }) => (
  <div className="flex p-2 [&_a]:ml-1">
    <span className="font-bold">Links: </span>
    <span>{children}</span>
  </div>
);

export function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
